import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { useTargetingResetOnAccountChange } from '@openx/components-targeting/Targeting/shared/hooks/useTargetingResetOnAccountChange.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { PackageFields } from '@openx/types/package.ts';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions.ts';
import { noop } from '@openx/utils/noop.ts';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePackageContext } from '../../../utils/context/PackageContext.tsx';
import { packageLabels } from '../constants.ts';

type OnBehalfOfFormProps = {
  isEdit: boolean;
  setSelectedAccountId?: (accountId: string) => void;
};

export function OnBehalfOfForm({ isEdit, setSelectedAccountId = noop }: OnBehalfOfFormProps) {
  const { t } = useTranslation();

  const {
    setValue,
    formState: { isSubmitting },
  } = useFormContext();

  const { useAccountOptionsFetch, useDispatchGetNetworkAndPublishersAccountsOptions } = usePackageContext();

  const { accounts } = useAccountOptionsFetch();

  const observableAccountUid = useWatch({ name: PackageFields.ACCOUNT_UID });

  useDispatchGetNetworkAndPublishersAccountsOptions();

  const labels = useMemo(() => packageLabels(t), [t]);
  const accountOptions = useMemo(() => produceSortedOptionsKeys(accounts.mapById), [accounts]);

  const { resetTargeting } = useTargetingResetOnAccountChange('package');

  const handleOnManageAccountChange = useCallback(
    (_, value) => {
      setValue(PackageFields.ACCOUNT_UID, value);
      setSelectedAccountId(value);
      resetTargeting();
    },
    [setSelectedAccountId, setValue, resetTargeting]
  );

  return (
    <FormSection
      divider
      title={t('On Behalf Of')}
      description={t('Create package on behalf of a different account.')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
      name="OnBehalfOfForm"
    >
      <RHFAutoCompleteVirtualize
        name={PackageFields.ACCOUNT_UID}
        textFieldProps={{ label: labels[PackageFields.ACCOUNT_UID] }}
        disableClearable
        renderOptions={{
          dataTest: 'package-account-item',
          tooltip: true,
        }}
        disabled={isEdit || isSubmitting}
        getOptionLabel={option => accounts.mapById[option]?.name || option}
        options={accountOptions}
        loading={accounts.loading}
        value={accounts.loading ? null : observableAccountUid}
        onChange={handleOnManageAccountChange}
        data-test="package-account"
      />
    </FormSection>
  );
}
